import { at } from './at.js';
export const EARTH_METERS_PER_DEGREE_LAT = 110574;
export function metersPerDegreeLon(latitude) {
    return 111320 * Math.cos((latitude * Math.PI) / 180);
}
export function pixelToLonLat(affine, pixel) {
    return {
        lon: affine.a * pixel.col + affine.b * pixel.row + affine.c,
        lat: affine.d * pixel.col + affine.e * pixel.row + affine.f,
    };
}
/**
 * Inverse de la transformation affine.
 * Le cas général (b et d non nuls) est traité : une orthophoto peut être calée
 * avec une rotation, et supposer b = d = 0 marcherait jusqu'au jour où non.
 */
export function lonLatToPixel(affine, point) {
    const { a, b, c, d, e, f } = affine;
    const det = a * e - b * d;
    if (Math.abs(det) < Number.EPSILON) {
        throw new Error('Transformation affine non inversible : déterminant nul.');
    }
    const dx = point.lon - c;
    const dy = point.lat - f;
    return { col: (e * dx - b * dy) / det, row: (a * dy - d * dx) / det };
}
/** Distance en mètres. Équirectangulaire : suffisant et stable à cette échelle. */
export function distanceMeters(from, to) {
    const midLat = (from.lat + to.lat) / 2;
    const dx = (to.lon - from.lon) * metersPerDegreeLon(midLat);
    const dy = (to.lat - from.lat) * EARTH_METERS_PER_DEGREE_LAT;
    return Math.hypot(dx, dy);
}
/** Cap en degrés depuis le nord, sens horaire. */
export function bearingDegrees(from, to) {
    const midLat = (from.lat + to.lat) / 2;
    const dx = (to.lon - from.lon) * metersPerDegreeLon(midLat);
    const dy = (to.lat - from.lat) * EARTH_METERS_PER_DEGREE_LAT;
    const deg = (Math.atan2(dx, dy) * 180) / Math.PI;
    return (deg + 360) % 360;
}
/** Point à `distance` mètres dans la direction `bearing`. */
export function destination(origin, bearingDeg, distanceM) {
    const rad = (bearingDeg * Math.PI) / 180;
    const dx = Math.sin(rad) * distanceM;
    const dy = Math.cos(rad) * distanceM;
    return {
        lon: origin.lon + dx / metersPerDegreeLon(origin.lat),
        lat: origin.lat + dy / EARTH_METERS_PER_DEGREE_LAT,
    };
}
/** Interpolation linéaire entre deux points, `t` dans [0, 1]. */
export function interpolate(from, to, t) {
    const clamped = Math.max(0, Math.min(1, t));
    return {
        lon: from.lon + (to.lon - from.lon) * clamped,
        lat: from.lat + (to.lat - from.lat) * clamped,
    };
}
export function buildPolyline(points) {
    if (points.length < 2) {
        throw new Error('Une polyligne exige au moins deux points.');
    }
    const cumulative = [0];
    for (let i = 1; i < points.length; i += 1) {
        cumulative.push(at(cumulative, i - 1) + distanceMeters(at(points, i - 1), at(points, i)));
    }
    return {
        points: [...points],
        cumulative,
        totalMeters: at(cumulative, cumulative.length - 1),
    };
}
/** Position à `distanceM` mètres du départ, le long de la polyligne. */
export function positionAlong(line, distanceM) {
    if (distanceM <= 0) {
        return {
            point: at(line.points, 0),
            headingDeg: bearingDegrees(at(line.points, 0), at(line.points, 1)),
            arrived: false,
        };
    }
    if (distanceM >= line.totalMeters) {
        const last = line.points.length - 1;
        return {
            point: at(line.points, last),
            headingDeg: bearingDegrees(at(line.points, last - 1), at(line.points, last)),
            arrived: true,
        };
    }
    let segment = 1;
    while (segment < line.cumulative.length && at(line.cumulative, segment) < distanceM)
        segment += 1;
    const from = at(line.points, segment - 1);
    const to = at(line.points, segment);
    const segmentStart = at(line.cumulative, segment - 1);
    const segmentLength = at(line.cumulative, segment) - segmentStart;
    const t = segmentLength === 0 ? 0 : (distanceM - segmentStart) / segmentLength;
    return { point: interpolate(from, to, t), headingDeg: bearingDegrees(from, to), arrived: false };
}
/**
 * Inclusion par lancer de rayon.
 *
 * Le test est fait en degrés, pas en mètres : c'est équivalent pour une
 * appartenance et évite une conversion à chaque sommet.
 */
export function pointInRing(point, ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        const pi = at(ring, i);
        const pj = at(ring, j);
        const intersects = pi.lat > point.lat !== pj.lat > point.lat &&
            point.lon < ((pj.lon - pi.lon) * (point.lat - pi.lat)) / (pj.lat - pi.lat) + pi.lon;
        if (intersects)
            inside = !inside;
    }
    return inside;
}
/** Distance au bord du polygone, en mètres. Négative à l'intérieur. */
export function signedDistanceToRing(point, ring) {
    let minimum = Number.POSITIVE_INFINITY;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        minimum = Math.min(minimum, distanceToSegment(point, at(ring, j), at(ring, i)));
    }
    return pointInRing(point, ring) ? -minimum : minimum;
}
export function distanceToSegment(point, from, to) {
    const midLat = point.lat;
    const mx = metersPerDegreeLon(midLat);
    const my = EARTH_METERS_PER_DEGREE_LAT;
    const px = (point.lon - from.lon) * mx;
    const py = (point.lat - from.lat) * my;
    const sx = (to.lon - from.lon) * mx;
    const sy = (to.lat - from.lat) * my;
    const lengthSquared = sx * sx + sy * sy;
    if (lengthSquared === 0)
        return Math.hypot(px, py);
    const t = Math.max(0, Math.min(1, (px * sx + py * sy) / lengthSquared));
    return Math.hypot(px - t * sx, py - t * sy);
}
/** Rectangle orienté, décrit par son centre, son cap, sa longueur et sa largeur. */
export function orientedRectangle(center, headingDeg, lengthM, widthM) {
    const half = { length: lengthM / 2, width: widthM / 2 };
    const corners = [];
    for (const [alongSign, acrossSign] of [
        [1, -1],
        [1, 1],
        [-1, 1],
        [-1, -1],
    ]) {
        const alongPoint = destination(center, headingDeg, alongSign * half.length);
        corners.push(destination(alongPoint, (headingDeg + 90) % 360, acrossSign * half.width));
    }
    // Anneau fermé : le dernier point répète le premier, comme l'exige GeoJSON.
    corners.push({ ...at(corners, 0) });
    return corners;
}
/** Couloir rectangulaire autour d'un axe, de demi-largeur donnée. */
export function corridorRing(axisFrom, axisTo, halfWidthM) {
    const heading = bearingDegrees(axisFrom, axisTo);
    const left = (heading + 270) % 360;
    const right = (heading + 90) % 360;
    const ring = [
        destination(axisFrom, left, halfWidthM),
        destination(axisTo, left, halfWidthM),
        destination(axisTo, right, halfWidthM),
        destination(axisFrom, right, halfWidthM),
    ];
    ring.push({ ...at(ring, 0) });
    return ring;
}
export function ringCentroid(ring) {
    // Le dernier point répète le premier : l'inclure biaiserait la moyenne.
    const points = ring.length > 1 &&
        at(ring, 0).lon === at(ring, ring.length - 1).lon &&
        at(ring, 0).lat === at(ring, ring.length - 1).lat
        ? ring.slice(0, -1)
        : ring;
    const sum = points.reduce((acc, p) => ({ lon: acc.lon + p.lon, lat: acc.lat + p.lat }), {
        lon: 0,
        lat: 0,
    });
    return { lon: sum.lon / points.length, lat: sum.lat / points.length };
}
/** Anneau au format GeoJSON : [[lon, lat], …]. */
export function ringToGeoJson(ring) {
    return ring.map((p) => [p.lon, p.lat]);
}
//# sourceMappingURL=geo.js.map